import { Component, ElementRef, ViewChild } from '@angular/core';
import { ChatbotService, ChatTurn, ChatResult, ChatSessionSummary, ChatSessionMessage } from 'src/app/services/chatbot.service';
import { UserSessionService } from 'src/app/services/usersession.service';

interface ChatMessage {
  from: 'user' | 'bot';
  text: string;
  isError?: boolean;
  result?: ChatResult;
  time: Date;
}

// One saved thread: a row of the chatbot service's chat_sessions table.
// Its messages (query_logs rows) are fetched only when the thread is opened.
interface Conversation {
  id: string;
  title: string;
  updated: Date;
  messageCount: number;
}

@Component({
  selector: 'app-chatbot',
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.scss']
})
export class ChatbotComponent {
  @ViewChild('scroller') scroller: ElementRef;
  @ViewChild('panel') panel: ElementRef;

  /** Set once the user drags/resizes the panel; null = default bottom-right anchoring. */
  box: { left: number; top: number; width: number; height: number } | null = null;
  private readonly minW = 320;
  private readonly minH = 400;

  open = false;
  maximized = false;
  showHistory = false;
  fontScales = [0.85, 1, 1.2, 1.4, 1.6];
  fontLabels = ['Small', 'Medium', 'Large', 'X-Large', 'Huge'];
  fontIdx = 1;
  dark = false;
  history: Conversation[] = [];
  historySearch = '';
  convId: string | null = null;
  loadingHistory = false;
  busy = false;
  input = '';
  sessionId: string | null = null;
  remaining: number | null = null;
  limit: number | null = null;
  messages: ChatMessage[] = [];
  suggestions = [
    'Show today\'s bookings',
    'Machinery count by district',
    'Subsidy released this month'
  ];

  constructor(public chat: ChatbotService, private session: UserSessionService) {
    try {
      this.dark = localStorage.getItem('aed_chat_theme') === 'dark';
    } catch (e) { }
    try {
      const i = parseInt(localStorage.getItem('aed_chat_fontsize'), 10);
      if (i >= 0 && i < this.fontScales.length) { this.fontIdx = i; }
    } catch (e) { }
  }

  get fontScale(): number {
    return this.fontScales[this.fontIdx];
  }

  toggleTheme() {
    this.dark = !this.dark;
    try { localStorage.setItem('aed_chat_theme', this.dark ? 'dark' : 'light'); } catch (e) { }
  }

  changeFont(step: number) {
    this.fontIdx = Math.max(0, Math.min(this.fontScales.length - 1, this.fontIdx + step));
    try { localStorage.setItem('aed_chat_fontsize', String(this.fontIdx)); } catch (e) { }
  }

  /** Reads the saved threads from the chatbot service (chat_sessions table). */
  private loadHistory() {
    if (!this.chat.enabled) { return; }
    this.loadingHistory = true;
    this.chat.sessions().subscribe((list: ChatSessionSummary[]) => {
      this.loadingHistory = false;
      this.history = (list || []).map(x => ({
        id: x.sessionId,
        title: x.title,
        updated: new Date(x.updatedAt),
        messageCount: x.messageCount
      }));
    }, () => {
      this.loadingHistory = false;
      this.history = [];
    });
  }

  /** Rebuilds the on-screen messages from the logged turns (query_logs table). */
  private toMessages(turns: ChatSessionMessage[]): ChatMessage[] {
    const out: ChatMessage[] = [];
    (turns || []).forEach(t => {
      const time = new Date(t.createdAt);
      out.push({ from: 'user', text: t.question, time: time });
      out.push({ from: 'bot', text: t.message, result: t, time: time });
    });
    return out;
  }

  /** Moves the panel by its header; ignores presses on header buttons. */
  startDrag(ev: PointerEvent) {
    if (this.maximized || ev.button !== 0 || (ev.target as HTMLElement).closest('button, input')) { return; }
    const r = this.panel.nativeElement.getBoundingClientRect();
    const dx = ev.clientX - r.left;
    const dy = ev.clientY - r.top;
    this.track(ev, e => {
      const left = Math.max(0, Math.min(window.innerWidth - r.width, e.clientX - dx));
      const top = Math.max(0, Math.min(window.innerHeight - r.height, e.clientY - dy));
      this.box = { left, top, width: r.width, height: r.height };
    });
  }

  /** Resizes from the top-left corner, keeping the bottom-right corner fixed. */
  startResize(ev: PointerEvent) {
    if (ev.button !== 0) { return; }
    const r = this.panel.nativeElement.getBoundingClientRect();
    this.track(ev, e => {
      const width = Math.max(this.minW, Math.min(r.right, r.right - Math.max(0, e.clientX)));
      const height = Math.max(this.minH, Math.min(r.bottom, r.bottom - Math.max(0, e.clientY)));
      this.box = { left: r.right - width, top: r.bottom - height, width, height };
    });
  }

  private track(start: PointerEvent, move: (e: PointerEvent) => void) {
    start.preventDefault();
    const el = start.target as HTMLElement;
    el.setPointerCapture(start.pointerId);
    const up = () => {
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
      this.scrollDown();
    };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
  }

  toggleMaximize() {
    this.maximized = !this.maximized;
    this.scrollDown();
  }

  toggleHistory() {
    this.showHistory = !this.showHistory;
    this.historySearch = '';
    if (this.showHistory) { this.loadHistory(); }
  }

  /** History filtered by the search box and split into Today / Yesterday / Previous 7 days / Previous 30 days / Older. */
  get historyGroups(): { label: string; items: Conversation[] }[] {
    const q = this.historySearch.trim().toLowerCase();
    const list = this.history
      .filter(c => !q || (c.title || '').toLowerCase().indexOf(q) >= 0)
      .sort((a, b) => b.updated.getTime() - a.updated.getTime());
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const day = 86400000;
    const buckets = [
      { label: 'Today', min: startOfToday.getTime(), items: [] as Conversation[] },
      { label: 'Yesterday', min: startOfToday.getTime() - day, items: [] as Conversation[] },
      { label: 'Previous 7 days', min: startOfToday.getTime() - 7 * day, items: [] as Conversation[] },
      { label: 'Previous 30 days', min: startOfToday.getTime() - 30 * day, items: [] as Conversation[] },
      { label: 'Older', min: -Infinity, items: [] as Conversation[] }
    ];
    list.forEach(c => buckets.find(b => c.updated.getTime() >= b.min).items.push(c));
    return buckets.filter(b => b.items.length);
  }

  openConversation(c: Conversation) {
    if (this.busy) { return; }
    this.busy = true;
    this.chat.sessionMessages(c.id).subscribe(turns => {
      this.busy = false;
      this.convId = c.id;
      this.sessionId = c.id;
      this.messages = this.toMessages(turns);
      this.showHistory = false;
      this.scrollDown();
    }, () => {
      this.busy = false;
      this.messages.push({
        from: 'bot',
        text: 'Unable to load that conversation. Please try again later.',
        isError: true,
        time: new Date()
      });
    });
  }

  /** First word of the logged-in user's name, for the welcome heading; empty when the token has none. */
  get firstName(): string {
    try { return (this.session.getUserName() || '').trim().split(/\s+/)[0]; } catch (e) { return ''; }
  }

  get isFresh(): boolean {
    return this.messages.length === 0 && !this.busy;
  }

  get quotaPct(): number {
    return this.limit ? Math.max(0, Math.min(100, Math.round((this.remaining / this.limit) * 100))) : 100;
  }

  toggle() {
    this.open = !this.open;
    this.scrollDown();
  }

  close() {
    this.open = false;
  }

  useSuggestion(text: string) {
    this.input = text;
    this.send();
  }

  trackByIndex(i: number) {
    return i;
  }

  send() {
    const q = (this.input || '').trim();
    if (!q || this.busy) { return; }

    // Every question stands alone: no earlier turns are sent to the backend.
    const history: ChatTurn[] = [];

    this.messages.push({ from: 'user', text: q, time: new Date() });
    this.input = '';
    this.busy = true;
    this.scrollDown();

    this.chat.ask(q, this.sessionId, history).subscribe(res => {
      this.busy = false;
      const quota = res.quota || (res.value && res.value[0] && res.value[0].quota);
      if (quota && quota.enforced) {
        this.remaining = quota.remaining;
        this.limit = quota.limit;
      }
      const r = res.value && res.value.length ? res.value[0] : null;
      if (r && r.sessionId) { this.sessionId = r.sessionId; this.convId = r.sessionId; }

      if (res.isSuccess && r) {
        this.messages.push({ from: 'bot', text: r.message, result: r, time: new Date() });
      } else {
        // failureReason is written to be shown to the user as-is
        this.messages.push({
          from: 'bot',
          text: res.failureReason || (r && r.message) || 'Something went wrong. Please try again.',
          isError: true,
          time: new Date()
        });
      }
      this.scrollDown();
    }, () => {
      this.busy = false;
      this.messages.push({
        from: 'bot',
        text: 'Unable to reach the reporting service. Please try again later.',
        isError: true,
        time: new Date()
      });
      this.scrollDown();
    });
  }

  columns(r: ChatResult): string[] {
    return r.columns || [];
  }

  cell(row: any, col: string): string {
    const v = row[col];
    return v === null || v === undefined ? '' : String(v);
  }

  download(r: ChatResult) {
    window.open(this.chat.absoluteUrl(r.report.downloadUrl), '_blank');
  }

  newChat() {
    if (this.busy) { return; }
    this.sessionId = null;
    this.convId = null;
    this.input = '';
    this.messages = [];
    this.showHistory = false;
  }

  private scrollDown() {
    setTimeout(() => {
      if (this.scroller) {
        this.scroller.nativeElement.scrollTop = this.scroller.nativeElement.scrollHeight;
      }
    }, 0);
  }
}
